import { Component } from '@theme/component';
import { debounce, fetchConfig } from '@theme/utilities';

/**
 * <order-notes> — the cart's Special Instructions / Gift fields.
 *
 * Both fields write ONE sectioned `cart.note` (format below), so the checkout
 * extension in Workers/birch-order-management (extensions/order-notes) can
 * show the same values and either place can edit either field. Whether this
 * renders at all is decided in Liquid (snippets/order-notes.liquid) from the
 * `birch_om.order_notes` shop metafield the OM "Order Notes" tab writes.
 *
 * The gift-email inputs (recipient email / include items — the
 * `birch_om.gifting` metafield, rendered by the same snippet) write cart
 * ATTRIBUTES, never the note: the note reaches vendors and packing slips.
 * The recipient email is REQUIRED while the gift box is ticked: the Checkout
 * button of this cart (drawer or page) is intercepted and the error shown
 * until a usable address is entered — see #onCheckoutClick. (With the tab's
 * Text messages switch on, the snippet marks the input data-phone="true" and
 * the field also takes a mobile number, and the consent line under it shows
 * only while the entry is one; labels live in locales/en.default.json.)
 *
 * The section between the CORE markers is a verbatim copy of
 * Workers/birch-order-management/src/order-notes/notes-core.mjs — edit there
 * first, then paste here.
 */

/* ---- CORE (copied from birch-order-management notes-core.mjs) ---------- */
// Order notes — the pure core shared by every surface that reads or writes the
// order note: the theme cart (birch-shopify-web/assets/order-notes.js), the
// checkout UI extension (extensions/order-notes/src/notes-core.mjs) and the
// worker's admin/tests. The two copies are byte-identical to this file; edit
// here first, then copy — see README.md § Order notes.
//
// FORMAT — one `cart.note` / `order.note` string, sectioned so the cart and
// checkout can each edit one field without clobbering the other:
//
//   Special Instructions:
//   <free text>
//
//   Gift Message:
//   <gift message, or "Yes" when flagged with no message>
//
//   Gift Services:
//   <per-vendor instructions the worker appends AFTER the order is placed>
//
// A section is emitted only when it has content, always in that order. Any
// text that is not under one of our headings (a note written by something
// else) is kept verbatim ahead of the sections so nothing is destroyed.
//
// Orders placed before 2026-10-26 carry `Gift Option:` for the gift section;
// it still parses (LEGACY_HEADINGS) and is rewritten as `Gift Message:` the
// next time the note is serialized.
//
// The cart and checkout only ever write the first two; `Gift Services:` is
// written by the worker's orders/create handler (src/gifting/) onto the ORDER
// note from the gift-services allowlists, and is parsed here so a later
// rewrite of the note never loses it.

export const HEADINGS = Object.freeze({
  instructions: "Special Instructions:",
  gift: "Gift Message:",
  services: "Gift Services:",
});

/** Earlier spellings of a heading, still read but never written. */
const LEGACY_HEADINGS = Object.freeze({ gift: ["Gift Option:"] });

/** Emitted for a gift-flagged order whose shopper left the message blank. */
export const GIFT_NO_MESSAGE = "Yes";

const SECTION_ORDER = ["instructions", "gift", "services"];

// Pre-2026-09-12 theme format ("GIFT ORDER" marker + optional "Message: …"),
// migrated into the gift section so a cart carried across the deploy keeps
// its state. See git: birch-shopify-web acdb896.
const LEGACY_GIFT_PREFIX = "GIFT ORDER";
const LEGACY_MESSAGE_MARKER = "Message: ";

/**
 * @typedef {{ instructions: string, gift: string, services: string, other: string }} NoteParts
 */

/**
 * Split a note into its sections. Every part comes back trimmed; missing
 * sections are "". `other` is whatever preceded the first heading.
 * @param {string | null | undefined} note
 * @returns {NoteParts}
 */
export function parseNote(note) {
  const parts = { instructions: "", gift: "", services: "", other: "" };
  const text = String(note ?? "").replace(/\r\n?/g, "\n");
  if (!text.trim()) return parts;

  let current = "other";
  const buf = { instructions: [], gift: [], services: [], other: [] };
  for (const line of text.split("\n")) {
    const heading = headingFor(line);
    if (heading) {
      current = heading;
      continue;
    }
    buf[current].push(line);
  }
  for (const key of Object.keys(buf)) parts[key] = buf[key].join("\n").trim();

  if (parts.other.startsWith(LEGACY_GIFT_PREFIX)) {
    const legacy = parts.other;
    parts.other = "";
    if (!parts.gift) {
      const at = legacy.indexOf(LEGACY_MESSAGE_MARKER);
      parts.gift = at >= 0 ? legacy.slice(at + LEGACY_MESSAGE_MARKER.length).trim() : "";
      if (!parts.gift) parts.gift = GIFT_NO_MESSAGE;
    }
  }
  return parts;
}

/** @param {string} line @returns {keyof typeof HEADINGS | null} */
function headingFor(line) {
  const t = line.trim();
  for (const key of SECTION_ORDER) {
    if (t === HEADINGS[key] || LEGACY_HEADINGS[key]?.includes(t)) return key;
  }
  return null;
}

/**
 * Build the note string. Empty sections are dropped; an all-empty input
 * yields "" (which clears the note on the cart).
 * @param {Partial<NoteParts>} parts
 * @returns {string}
 */
export function serializeNote(parts) {
  const chunks = [];
  const other = clean(parts.other);
  if (other) chunks.push(other);
  for (const key of SECTION_ORDER) {
    const body = clean(parts[key]);
    if (body) chunks.push(`${HEADINGS[key]}\n${body}`);
  }
  return chunks.join("\n\n");
}

/** @param {unknown} s */
function clean(s) {
  return String(s ?? "").replace(/\r\n?/g, "\n").trim();
}

/**
 * Update one section of an existing note, leaving the rest intact.
 * @param {string | null | undefined} note
 * @param {Partial<NoteParts>} patch
 */
export function withSection(note, patch) {
  return serializeNote({ ...parseNote(note), ...patch });
}

// ---------------------------------------------------------------------------
// Config + eligibility. The config object is what the OM admin tab saves to D1
// and mirrors onto the `birch_om.order_notes` shop metafield, which is how the
// theme (Liquid) and the checkout extension (appMetafields) read it.

/**
 * @typedef {"all" | "vendors" | "products" | "except_vendors" | "except_products"} RuleScope
 *   all              every cart
 *   vendors          only when the cart holds a product from a listed vendor
 *   products         only when the cart holds a listed product
 *   except_vendors   every cart EXCEPT one holding a product from a listed vendor
 *   except_products  every cart EXCEPT one holding a listed product
 * The two `except_*` scopes hide on ANY matching line — a cart that is only
 * partly excluded is treated as excluded.
 */

/**
 * @typedef {object} FieldRule
 * @property {boolean} enabled
 * @property {RuleScope} scope
 * @property {string[]} vendors          vendor names (exact, as Shopify reports them)
 * @property {string[]} productIds       numeric product ids as strings
 */

/**
 * @typedef {object} OrderNotesConfig
 * @property {boolean} enabled           master switch
 * @property {FieldRule} instructions    Special Instructions — its own on/off + scope
 * @property {FieldRule} gift            Gift option — its own on/off + scope
 * @property {number} updatedAt          epoch ms
 */

export const FIELD_KEYS = Object.freeze(["instructions", "gift"]);

/**
 * Cart/order attribute the registry app sets on a registry-gift cart
 * (birch-shopify-web/assets/registry-store.js, birch-registry orders/paid).
 * A registry gift already carries its own gift note (line-item property
 * "Gift note", emailed to the registrant by Registry), so the OM gift option
 * — and with it the gift email + services — never shows on such a cart.
 */
export const REGISTRY_ATTRIBUTE = "_registry_id";

/**
 * @param {Record<string, unknown> | Array<{ key?: string, name?: string, value?: unknown }> | null | undefined} attributes
 *   cart attributes as a map (theme), a `{key, value}` list (checkout) or a
 *   `{name, value}` list (order webhook).
 */
export function isRegistryCart(attributes) {
  if (!attributes) return false;
  if (Array.isArray(attributes)) {
    return attributes.some((a) => (a?.key ?? a?.name) === REGISTRY_ATTRIBUTE && String(a?.value ?? "").trim() !== "");
  }
  return String(attributes[REGISTRY_ATTRIBUTE] ?? "").trim() !== "";
}

/** @param {boolean} enabled @returns {FieldRule} */
function defaultRule(enabled) {
  return { enabled, scope: "all", vendors: [], productIds: [] };
}

/** @returns {OrderNotesConfig} */
export function defaultConfig() {
  return {
    enabled: false,
    instructions: defaultRule(true),
    gift: defaultRule(true),
    updatedAt: 0,
  };
}

const SCOPES = new Set(["all", "vendors", "products", "except_vendors", "except_products"]);

/**
 * Coerce an untrusted object (metafield JSON, admin PUT body) into a config.
 * Unknown keys are dropped, bad shapes fall back to the default for that key.
 * Also reads the pre-2026-09-13 shape (one shared `scope`/`vendors`/
 * `productIds` + `fields: {instructions, gift}`) so a metafield written by the
 * previous worker still parses.
 * @param {unknown} raw
 * @returns {OrderNotesConfig}
 */
export function normalizeConfig(raw) {
  const o = raw && typeof raw === "object" ? /** @type {Record<string, unknown>} */ (raw) : {};
  const legacyFields = o.fields && typeof o.fields === "object" ? /** @type {Record<string, unknown>} */ (o.fields) : null;
  const legacyRule = legacyFields || "scope" in o ? normalizeRule(o, true) : null;

  /** @param {string} key */
  const rule = (key) => {
    const own = o[key];
    if (own && typeof own === "object") return normalizeRule(/** @type {Record<string, unknown>} */ (own), true);
    if (legacyRule) return { ...legacyRule, enabled: !legacyFields || legacyFields[key] !== false };
    return defaultRule(true);
  };

  return {
    enabled: o.enabled === true,
    instructions: rule("instructions"),
    gift: rule("gift"),
    updatedAt: typeof o.updatedAt === "number" && Number.isFinite(o.updatedAt) ? o.updatedAt : 0,
  };
}

/**
 * @param {Record<string, unknown>} o
 * @param {boolean} enabledDefault
 * @returns {FieldRule}
 */
function normalizeRule(o, enabledDefault) {
  return {
    enabled: o.enabled === undefined ? enabledDefault : o.enabled === true,
    scope: SCOPES.has(String(o.scope)) ? /** @type {any} */ (o.scope) : "all",
    vendors: strList(o.vendors),
    productIds: strList(o.productIds).map(stripGid),
  };
}

/** @param {string} id */
function stripGid(id) {
  return String(id).replace(/^gid:\/\/shopify\/Product\//, "");
}

/** @param {unknown} v @returns {string[]} */
function strList(v) {
  if (!Array.isArray(v)) return [];
  const out = [];
  for (const x of v) {
    const s = String(x ?? "").trim();
    if (s && !out.includes(s)) out.push(s);
  }
  return out;
}

/**
 * Does a cart satisfy one field's rule? `lines` is the cart's merchandise in
 * whatever shape the surface has — only `vendor` and `productId` are read.
 * @param {FieldRule} rule
 * @param {Array<{ vendor?: string | null, productId?: string | number | null }>} lines
 */
export function ruleApplies(rule, lines) {
  if (!rule?.enabled) return false;
  switch (rule.scope) {
    case "vendors":
      return anyVendor(rule, lines);
    case "products":
      return anyProduct(rule, lines);
    case "except_vendors":
      return !anyVendor(rule, lines);
    case "except_products":
      return !anyProduct(rule, lines);
    default:
      return true;
  }
}

/** @param {FieldRule} rule @param {Array<{ vendor?: string | null }>} lines */
function anyVendor(rule, lines) {
  const want = new Set(rule.vendors.map(norm));
  return lines.some((l) => want.has(norm(l.vendor)));
}

/** @param {FieldRule} rule @param {Array<{ productId?: string | number | null }>} lines */
function anyProduct(rule, lines) {
  const want = new Set(rule.productIds.map(stripGid));
  return lines.some((l) => l.productId != null && want.has(stripGid(String(l.productId))));
}

/**
 * Which fields to show for a given cart — each field judged by its own rule.
 * Returns null when nothing should render. The gift field is withheld on a
 * registry cart (see REGISTRY_ATTRIBUTE) whatever its rule says.
 *
 * @param {OrderNotesConfig} config
 * @param {Array<{ vendor?: string | null, productId?: string | number | null }>} lines
 * @param {Parameters<typeof isRegistryCart>[0]} [attributes]  cart attributes, when the surface has them
 * @returns {{ instructions: boolean, gift: boolean } | null}
 */
export function fieldsFor(config, lines, attributes) {
  if (!config?.enabled) return null;
  const fields = {
    instructions: ruleApplies(config.instructions, lines),
    gift: ruleApplies(config.gift, lines) && !isRegistryCart(attributes),
  };
  return fields.instructions || fields.gift ? fields : null;
}

/** @param {unknown} s */
function norm(s) {
  return String(s ?? "").trim().toLowerCase();
}

// ---------------------------------------------------------------------------
// Gifting — the second config object the OM Order Notes tab saves (D1
// `gifting_config`, mirrored onto the `birch_om.gifting` shop metafield).
// Managed separately from the note fields above: the gift EMAIL and the gift
// SERVICES each have their own master switch, both off by default.
//
// Gift email: when the shopper ticks "This order is a gift" and the email rule
// is in scope, the cart/checkout also REQUIRE the recipient's email address
// (one field; classifyGiftContact validates it) plus optionally whether to
// list the items. Those land as CART ATTRIBUTES, never in the note — the note
// is printed on packing slips and forwarded to vendors. The worker
// (src/gifting/) emits a Klaviyo event at the configured moment; a Klaviyo
// flow sends it.
//
// The same field takes a mobile number when the gifting config's "Text
// messages" switch (`sms.enabled`, Order Notes tab) is on, with a buyer-consent
// line under it. Every surface passes that switch to classifyGiftContact as
// `phoneAllowed`; off, a phone is rejected like any bad email. The worker sends
// the text itself (Twilio), because Klaviyo will not text an unconsented number.
//
// Gift services: per-service allowlists of vendors that can print the note /
// gift wrap / include a return label. No storefront UI — on a gift order the
// worker writes a `Gift Services:` note section + tags for the vendors that
// can.

/**
 * Cart/order attribute keys. Underscore-prefixed so Shopify hides them from
 * the customer-facing cart, checkout and order-status pages (staff still see
 * them under "Additional details" on the order).
 */
export const GIFT_ATTRIBUTES = Object.freeze({
  // Exactly one of the two recipient keys is set (see giftContactAttributes).
  recipientEmail: "_gift_recipient_email",
  recipientPhone: "_gift_recipient_phone",
  // No longer collected (the "From" input was removed 2026-09-21); still read
  // by the worker so a cart that carried it keeps its sender line.
  senderName: "_gift_sender_name",
  includeItems: "_gift_include_items",
});

/** Longest recipient contact accepted (an email; a phone is far shorter). */
export const GIFT_CONTACT_MAX = 254;

const GIFT_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Work out what the shopper typed into the recipient field. An "@" makes it
 * an email (lower-cased, checked against GIFT_EMAIL_RE). With `phoneAllowed`
 * (the Text messages switch), anything else is read as a phone number and normalised
 * to E.164: separators stripped, a bare 10-digit number taken as US/Canada
 * (+1), a bare 11–15-digit number given a "+". Returns kind null (value "")
 * when the input is blank or fits no accepted shape — the storefront shows
 * its error then.
 *
 * @param {unknown} raw
 * @param {boolean} [phoneAllowed]  the gifting config's sms.enabled; default off
 * @returns {{ kind: "email" | "phone" | null, value: string }}
 */
export function classifyGiftContact(raw, phoneAllowed = false) {
  const text = String(raw ?? "").trim();
  if (!text || text.length > GIFT_CONTACT_MAX) return { kind: null, value: "" };
  if (text.includes("@")) {
    const email = text.toLowerCase();
    return GIFT_EMAIL_RE.test(email) ? { kind: "email", value: email } : { kind: null, value: "" };
  }
  if (!phoneAllowed) return { kind: null, value: "" };
  // Only digits, "+", and the usual separators may appear in a phone number.
  if (!/^\+?[\d\s().-]+$/.test(text)) return { kind: null, value: "" };
  const plus = text.startsWith("+");
  const digits = text.replace(/\D/g, "");
  if (plus) {
    return digits.length >= 8 && digits.length <= 15 ? { kind: "phone", value: `+${digits}` } : { kind: null, value: "" };
  }
  if (digits.length === 10) return { kind: "phone", value: `+1${digits}` };
  if (digits.length >= 11 && digits.length <= 15) return { kind: "phone", value: `+${digits}` };
  return { kind: null, value: "" };
}

/**
 * Is the shopper typing a PHONE number into the recipient contact field (as
 * opposed to an email)? Only the SMS consent line uses this: that line is
 * about adding a mobile number, so it must appear while one is being typed
 * and stay away while an email is. Deliberately looser than
 * classifyGiftContact — a half-typed "212 555" is already a phone entry —
 * and false for anything blank, lettered or carrying an "@".
 *
 * @param {unknown} raw   what the shopper typed so far
 * @returns {boolean}
 */
export function isGiftPhoneEntry(raw) {
  const text = String(raw ?? "").trim();
  if (!text || text.length > GIFT_CONTACT_MAX) return false;
  if (!/\d/.test(text)) return false;
  return /^\+?[\d\s().-]+$/.test(text);
}

/**
 * The two recipient attributes for one typed contact: the matching key holds
 * the normalised value, the other is "" (which removes it from the cart).
 * Both "" when the contact is blank/invalid or the gift box is off.
 * @param {unknown} raw   what the shopper typed; pass "" to clear
 * @param {boolean} [phoneAllowed]
 * @returns {Record<string, string>}
 */
export function giftContactAttributes(raw, phoneAllowed = false) {
  const c = classifyGiftContact(raw, phoneAllowed);
  return {
    [GIFT_ATTRIBUTES.recipientEmail]: c.kind === "email" ? c.value : "",
    [GIFT_ATTRIBUTES.recipientPhone]: c.kind === "phone" ? c.value : "",
  };
}

/**
 * The recipient an order/cart carries, read back from its attributes (either
 * shape isRegistryCart accepts). Email wins if — against giftContactAttributes'
 * contract — both are present. Null when neither is usable.
 * @param {Parameters<typeof isRegistryCart>[0]} attributes
 * @param {boolean} [phoneAllowed]  false → a phone attribute is ignored
 * @returns {{ channel: "email" | "sms", recipient: string } | null}
 */
export function giftRecipientOf(attributes, phoneAllowed = false) {
  /** @param {string} key */
  const get = (key) => {
    if (!attributes) return "";
    if (Array.isArray(attributes)) {
      const hit = attributes.find((a) => (a?.key ?? a?.name) === key);
      return String(hit?.value ?? "");
    }
    return String(attributes[key] ?? "");
  };
  const email = classifyGiftContact(get(GIFT_ATTRIBUTES.recipientEmail));
  if (email.kind === "email") return { channel: "email", recipient: email.value };
  // classifyGiftContact refuses phones unless phoneAllowed.
  const phone = classifyGiftContact(get(GIFT_ATTRIBUTES.recipientPhone), phoneAllowed);
  if (phone.kind === "phone") return { channel: "sms", recipient: phone.value };
  return null;
}

/** When the gift email goes out. */
export const GIFT_EMAIL_TRIGGERS = Object.freeze(["placed", "fulfillment", "fulfilled_all", "delivery"]);

export const GIFT_SERVICE_KEYS = Object.freeze(["printNote", "giftWrap", "returnLabel"]);

/**
 * @typedef {object} GiftEmailConfig
 * @property {boolean} enabled
 * @property {FieldRule} rule            when to ASK for a recipient email (scoped like a note field)
 * @property {"placed" | "fulfillment" | "fulfilled_all" | "delivery"} trigger
 * @property {boolean} includeItems      list the items (never prices) in the email
 * @property {boolean} shopperChoosesItems  surface an "include what's inside" checkbox at checkout
 */

/**
 * @typedef {object} GiftService
 * @property {boolean} enabled
 * @property {string[]} vendors          allowlist — vendors confirmed to support the service
 */

/**
 * @typedef {object} GiftingConfig
 * @property {GiftEmailConfig} email
 * @property {{ enabled: boolean }} sms   Text messages: the recipient field also takes a mobile number, texted via Twilio
 * @property {{ enabled: boolean, printNote: GiftService, giftWrap: GiftService, returnLabel: GiftService }} services
 * @property {number} updatedAt
 */

/** @returns {GiftingConfig} */
export function defaultGiftingConfig() {
  return {
    email: {
      enabled: false,
      rule: defaultRule(true),
      trigger: "placed",
      includeItems: true,
      shopperChoosesItems: false,
    },
    sms: { enabled: false },
    services: {
      enabled: false,
      printNote: { enabled: false, vendors: [] },
      giftWrap: { enabled: false, vendors: [] },
      returnLabel: { enabled: false, vendors: [] },
    },
    updatedAt: 0,
  };
}

/**
 * Coerce an untrusted object (metafield JSON, admin PUT body) into a
 * GiftingConfig. Unknown keys dropped, bad shapes fall back to defaults.
 * @param {unknown} raw
 * @returns {GiftingConfig}
 */
export function normalizeGiftingConfig(raw) {
  const o = raw && typeof raw === "object" ? /** @type {Record<string, any>} */ (raw) : {};
  const d = defaultGiftingConfig();
  const email = o.email && typeof o.email === "object" ? o.email : {};
  const services = o.services && typeof o.services === "object" ? o.services : {};

  /** @param {unknown} v @returns {GiftService} */
  const service = (v) => {
    const x = v && typeof v === "object" ? /** @type {Record<string, unknown>} */ (v) : {};
    return { enabled: x.enabled === true, vendors: strList(x.vendors) };
  };

  return {
    email: {
      enabled: email.enabled === true,
      rule: email.rule && typeof email.rule === "object" ? normalizeRule(email.rule, true) : d.email.rule,
      trigger: GIFT_EMAIL_TRIGGERS.includes(email.trigger) ? email.trigger : d.email.trigger,
      includeItems: email.includeItems === undefined ? d.email.includeItems : email.includeItems === true,
      shopperChoosesItems: email.shopperChoosesItems === true,
    },
    sms: { enabled: !!o.sms && typeof o.sms === "object" && o.sms.enabled === true },
    services: {
      enabled: services.enabled === true,
      printNote: service(services.printNote),
      giftWrap: service(services.giftWrap),
      returnLabel: service(services.returnLabel),
    },
    updatedAt: typeof o.updatedAt === "number" && Number.isFinite(o.updatedAt) ? o.updatedAt : 0,
  };
}

/**
 * Should the cart/checkout ask for (and require) a gift recipient contact?
 * Only meaningful once the shopper has ticked the gift box (the caller checks
 * that). Returns null when not, else which extra inputs to render.
 *
 * @param {GiftingConfig | null | undefined} config
 * @param {Array<{ vendor?: string | null, productId?: string | number | null }>} lines
 * @returns {{ includeItemsChoice: boolean, phoneEnabled: boolean } | null}
 */
export function giftEmailFieldsFor(config, lines) {
  const e = config?.email;
  if (!e?.enabled) return null;
  if (!ruleApplies(e.rule, lines)) return null;
  return { includeItemsChoice: e.shopperChoosesItems === true, phoneEnabled: config?.sms?.enabled === true };
}

/**
 * Which gift services each vendor on an order gets — the intersection of the
 * order's vendors with every enabled service's allowlist. Vendors with no
 * supported service are omitted. Order of vendors is preserved.
 *
 * @param {GiftingConfig | null | undefined} config
 * @param {Array<string | null | undefined>} vendors  one entry per line is fine; de-duplicated here
 * @returns {Array<{ vendor: string, services: Array<"printNote" | "giftWrap" | "returnLabel"> }>}
 */
export function giftServicesFor(config, vendors) {
  const s = config?.services;
  if (!s?.enabled) return [];
  const seen = new Set();
  const out = [];
  for (const raw of vendors) {
    const vendor = String(raw ?? "").trim();
    if (!vendor || seen.has(norm(vendor))) continue;
    seen.add(norm(vendor));
    const services = GIFT_SERVICE_KEYS.filter((k) => {
      const svc = s[k];
      return svc?.enabled && svc.vendors.some((v) => norm(v) === norm(vendor));
    });
    if (services.length) out.push({ vendor, services });
  }
  return out;
}

export const GIFT_SERVICE_LABELS = Object.freeze({
  printNote: "print gift note",
  giftWrap: "gift wrap",
  returnLabel: "include return label",
});

/**
 * Render the `Gift Services:` note body — one line per vendor so each vendor
 * can find its own instructions on a note every vendor sees.
 * @param {ReturnType<typeof giftServicesFor>} perVendor
 */
export function renderGiftServices(perVendor) {
  return perVendor.map((v) => `${v.vendor}: ${v.services.map((k) => GIFT_SERVICE_LABELS[k]).join(", ")}`).join("\n");
}
/* ---- END CORE ---------------------------------------------------------- */

class OrderNotes extends Component {
  /** @type {AbortController | null} */
  #activeFetch = null;
  /** Text that was on the note but not under our headings — preserved verbatim. */
  #other = '';
  /** The last note string we sent, so a re-render with the same note is a no-op. */
  #lastWritten = null;
  /** @type {AbortController | null} */
  #activeAttrFetch = null;
  /** The last attributes we sent, JSON, same no-op reasoning. */
  #lastAttrs = null;

  connectedCallback() {
    super.connectedCallback();
    // Seed `other` from the server-rendered note. The fields themselves are
    // pre-filled by Liquid; this is only the part Liquid does not surface.
    // No requiredRefs: the config may omit either field, so refs are checked
    // per use instead.
    const parsed = parseNote(this.dataset.note ?? '');
    this.#other = parsed.other;
    this.#lastWritten = this.dataset.note ?? '';
    // Liquid pre-filled the gift-contact input from cart.attributes; treat
    // that as already written so opening the drawer does not POST.
    if (this.#giftContactInput()) this.#lastAttrs = JSON.stringify(this.#serializeAttributes());
    // Capture on window so this runs ahead of component-cart-items' own
    // document-level checkout interception (partial checkout), which would
    // otherwise start navigating before the requirement is checked.
    window.addEventListener('click', this.#onCheckoutClick, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('click', this.#onCheckoutClick, true);
  }

  /** Which fields exist in this render (the config may hide either). */
  #fields() {
    return {
      instructions: this.refs.instructions instanceof HTMLTextAreaElement,
      gift: this.refs.giftToggle instanceof HTMLInputElement,
    };
  }

  #serialize() {
    const f = this.#fields();
    const instructions = f.instructions ? this.refs.instructions.value : '';
    let gift = '';
    if (f.gift && this.refs.giftToggle.checked) {
      const msg = this.refs.giftMessage instanceof HTMLTextAreaElement ? this.refs.giftMessage.value.trim() : '';
      gift = msg || GIFT_NO_MESSAGE;
    }
    return serializeNote({ other: this.#other, instructions, gift });
  }

  async #writeNote() {
    const note = this.#serialize();
    if (note === this.#lastWritten) return;
    this.#lastWritten = note;

    if (this.#activeFetch) this.#activeFetch.abort();
    const abortController = new AbortController();
    this.#activeFetch = abortController;
    try {
      const config = fetchConfig('json', { body: JSON.stringify({ note }) });
      await fetch(Theme.routes.cart_update_url, { ...config, signal: abortController.signal });
    } catch {
      // Aborted by a newer keystroke, or offline — the next write retries.
    } finally {
      if (this.#activeFetch === abortController) this.#activeFetch = null;
    }
  }

  /** @param {InputEvent} event */
  updateInstructions = debounce(() => this.#writeNote(), 250);

  /** @param {Event} event */
  toggleGift = (event) => {
    if (!(event.target instanceof HTMLInputElement)) return;
    const wrapper = this.refs.giftWrapper;
    if (event.target.checked) {
      wrapper?.removeAttribute('hidden');
      if (this.refs.giftMessage instanceof HTMLTextAreaElement) this.refs.giftMessage.focus();
    } else {
      wrapper?.setAttribute('hidden', '');
      // A cleared box owes nothing — drop any "required" error it showed.
      this.#setGiftContactError('');
    }
    this.#writeNote();
    // Un-ticking clears the recipient attributes; re-ticking restores them.
    this.#writeAttributes();
  };

  /** @param {InputEvent} event */
  updateGiftMessage = debounce(() => this.#writeNote(), 250);

  // ---- gift recipient email → cart attributes ----------------------------

  #giftContactInput() {
    return this.refs.giftContact instanceof HTMLInputElement ? this.refs.giftContact : null;
  }

  /** The Text messages switch, as the snippet rendered it onto the input. */
  #phoneAllowed() {
    return this.#giftContactInput()?.dataset.phone === 'true';
  }

  #giftOn() {
    return this.refs.giftToggle instanceof HTMLInputElement && this.refs.giftToggle.checked;
  }

  /**
   * The error the contact field is showing, or '' when it holds a usable
   * address. `requireValue` also flags an empty field (checkout time);
   * while typing/blurring only a malformed entry is flagged.
   * @param {boolean} requireValue
   */
  #giftContactProblem(requireValue) {
    const el = this.#giftContactInput();
    if (!el) return '';
    const v = el.value.trim();
    if (v === '') return requireValue ? 'required' : '';
    return classifyGiftContact(v, this.#phoneAllowed()).kind ? '' : 'invalid';
  }

  #serializeAttributes() {
    const el = this.#giftContactInput();
    const recipient = giftContactAttributes(this.#giftOn() && el ? el.value : '', this.#phoneAllowed());
    const hasRecipient = Object.values(recipient).some(Boolean);
    // Only recorded when the dashboard let the shopper choose (the checkbox
    // exists); otherwise the dashboard default applies at send time.
    const items = hasRecipient && this.refs.giftItems instanceof HTMLInputElement ? String(this.refs.giftItems.checked) : '';
    // '' removes the attribute on Shopify's side.
    return { ...recipient, [GIFT_ATTRIBUTES.includeItems]: items };
  }

  async #writeAttributes() {
    if (!this.#giftContactInput()) return;
    const attributes = this.#serializeAttributes();
    const key = JSON.stringify(attributes);
    if (key === this.#lastAttrs) return;
    this.#lastAttrs = key;

    if (this.#activeAttrFetch) this.#activeAttrFetch.abort();
    const abortController = new AbortController();
    this.#activeAttrFetch = abortController;
    try {
      const config = fetchConfig('json', { body: JSON.stringify({ attributes }) });
      await fetch(Theme.routes.cart_update_url, { ...config, signal: abortController.signal });
    } catch {
      // Aborted by a newer keystroke, or offline — the next write retries.
    } finally {
      if (this.#activeAttrFetch === abortController) this.#activeAttrFetch = null;
    }
  }

  /**
   * Show the items choice once there is a usable contact; show the SMS
   * consent line only while the entry being typed is a mobile number (never
   * for an email); clear the error while typing.
   */
  #syncGiftContactUi() {
    const el = this.#giftContactInput();
    if (!el) return;
    const extra = this.refs.giftContactExtra;
    if (extra instanceof HTMLElement) {
      if (classifyGiftContact(el.value, this.#phoneAllowed()).kind) extra.removeAttribute('hidden');
      else extra.setAttribute('hidden', '');
    }
    const consent = this.refs.giftContactConsent;
    if (consent instanceof HTMLElement) {
      if (this.#phoneAllowed() && isGiftPhoneEntry(el.value)) consent.removeAttribute('hidden');
      else consent.setAttribute('hidden', '');
    }
    if (!this.#giftContactProblem(false)) this.#setGiftContactError('');
  }

  /** @param {'' | 'required' | 'invalid'} problem */
  #setGiftContactError(problem) {
    const el = this.#giftContactInput();
    const err = this.refs.giftContactError;
    if (el) el.setAttribute('aria-invalid', problem ? 'true' : 'false');
    if (err instanceof HTMLElement) {
      if (problem) {
        err.textContent = problem === 'required' ? err.dataset.required ?? '' : err.dataset.invalid ?? '';
        err.removeAttribute('hidden');
      } else {
        err.setAttribute('hidden', '');
      }
    }
  }

  updateGiftContact = () => {
    this.#syncGiftContactUi();
    this.#debouncedWriteAttributes();
  };

  #debouncedWriteAttributes = debounce(() => this.#writeAttributes(), 250);

  /** On blur: flag a non-empty, malformed entry (never while typing). */
  validateGiftContact = () => {
    this.#setGiftContactError(this.#giftContactProblem(false));
  };

  /**
   * The requirement. A click on THIS cart's Checkout button (the drawer and
   * the cart page each render their own summary) is stopped while the gift
   * box is ticked and the contact is blank or malformed; the error shows and
   * the field takes focus (the gift fields are never inside an accordion, so
   * nothing needs opening first). Accelerated-checkout buttons (Shop Pay etc.) are
   * not interceptable here — checkout's own extension repeats the check.
   * @param {MouseEvent} event
   */
  #onCheckoutClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const checkoutBtn = target.closest('#checkout');
    if (!checkoutBtn) return;
    const myHost = this.closest('cart-drawer-component, .section');
    if (!myHost || checkoutBtn.closest('cart-drawer-component, .section') !== myHost) return;
    if (!this.#giftOn()) return;
    const problem = this.#giftContactProblem(true);
    if (!problem) {
      // Make sure the attribute the order will carry is the one on screen.
      this.#writeAttributes();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#setGiftContactError(problem);
    const el = this.#giftContactInput();
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.focus({ preventScroll: true });
  };
}

if (!customElements.get('order-notes')) {
  customElements.define('order-notes', OrderNotes);
}
